import React, { useCallback } from "react";
import { MDBContainer, MDBSpinner } from "mdb-react-ui-kit";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { useSetupGuideStatus } from "../../hooks/useSetupGuideStatus";
import { isCompanyTenantUser } from "../../utils/tenantUserType";
import { Tag } from "../ui";
import { getLocalizedPlatformName } from "../../utils/platformNames";
import {
  handlePlatformLogoError,
  resolveIntegrationLogo,
  resolvePlatformLogo,
  usesWideIntegrationLogo,
} from "../../utils/platformLogos";
import "./SetupGuidePage.css";

/** Same display name rule as /apps-management cards and table. */
function getDeliveryPlatformDisplayName(platform, t, isArabic) {
  if (isArabic && platform?.name_ar) return platform.name_ar;
  return (
    getLocalizedPlatformName(
      platform?.name || platform?.application || platform?.appType,
      t
    ) ||
    platform?.name ||
    ""
  );
}

/** Same rule as My Account / CompanyProfile.isComplete — N/A means unavailable. */
function hasMeaningfulBusinessInfo(data) {
  const meaningful = (value) => {
    const text = String(value ?? "").trim();
    if (!text) return false;
    const normalized = text.toLowerCase().replace(/\./g, "");
    if (normalized === "na" || normalized === "n/a" || normalized === "null") {
      return false;
    }
    return true;
  };
  return (
    meaningful(data?.crNumber) &&
    meaningful(data?.legalTradeName) &&
    meaningful(data?.registeredAddress)
  );
}

function getDeliveryPlatformStatusLabel(platform, ts) {
  if (platform.appStatus === "Coming Soon") return ts("deliveryTable.comingSoon");
  if (platform.appStatus === "Inactive") return ts("deliveryTable.inactive");
  return ts("deliveryTable.unavailable");
}

function DeliveryPlatformStatus({ platform, ts, t }) {
  if (!platform.connectable) {
    return (
      <span className="setup-guide-table__unavailable">
        {getDeliveryPlatformStatusLabel(platform, ts)}
      </span>
    );
  }

  if (platform.connected || platform.integrationStatus === "connected") {
    return <Tag variant="success">{t("connected")}</Tag>;
  }

  if (platform.integrationStatus === "disconnected") {
    return <Tag variant="error">{t("disconnected")}</Tag>;
  }

  return (
    <span className="setup-guide-table__unavailable">{ts("deliveryTable.notConnected")}</span>
  );
}

function StepCircle({ stepNumber, status, coreComplete }) {
  if (coreComplete || status === "complete") {
    return (
      <div className="setup-guide-step-circle setup-guide-step-circle--complete">
        <Check size={12} strokeWidth={3} />
      </div>
    );
  }
  if (status === "pending_approval" || status === "in_progress") {
    return (
      <div className="setup-guide-step-circle setup-guide-step-circle--active">
        {stepNumber}
      </div>
    );
  }
  return (
    <div className="setup-guide-step-circle setup-guide-step-circle--pending">
      {stepNumber}
    </div>
  );
}

function SubStepCircle({ stepNumber, complete }) {
  if (complete) {
    return (
      <div className="setup-guide-substep-circle setup-guide-substep-circle--complete">
        <Check size={10} strokeWidth={3} />
      </div>
    );
  }
  return <div className="setup-guide-substep-circle">{stepNumber}</div>;
}

function StatusText({ status, t }) {
  const map = {
    complete: ["setupGuide.status.completed", "setup-guide-status-text--complete"],
    pending_approval: ["setupGuide.status.pendingApproval", "setup-guide-status-text--progress"],
    in_progress: ["setupGuide.status.inProgress", "setup-guide-status-text--progress"],
    not_started: ["setupGuide.status.notStarted", "setup-guide-status-text--muted"],
    locked: ["setupGuide.status.locked", "setup-guide-status-text--muted"],
  };
  const [key, className] = map[status] || map.not_started;
  return <span className={`setup-guide-status-text ${className}`}>{t(key)}</span>;
}

function SetupGuidePage() {
  const { t, i18n } = useTranslation("translation");
  const navigate = useNavigate();
  const isArabic = i18n.language === "ar";
  const companyOnly = isCompanyTenantUser();
  const { steps, progress, loading } = useSetupGuideStatus({
    enabled: companyOnly,
  });

  const ts = useCallback((key, opts) => t(`setupGuide.${key}`, opts), [t]);

  if (!companyOnly) {
    return <Navigate to="/dashboard" replace />;
  }

  const goAddStore = () => navigate("/add-store");
  const goEditStore = () => {
    const storeId = steps?.store?.primaryStoreId;
    if (storeId) {
      navigate("/store&branches-overview", { state: { storeId } });
    } else {
      navigate("/store&branches-overview");
    }
  };
  const goAddBranch = () => {
    const storeId = steps?.branch?.primaryStoreId || steps?.store?.primaryStoreId;
    if (storeId) {
      navigate("/add-branch", { state: { storeId } });
    } else {
      navigate("/store&branches-overview");
    }
  };
  const goEditBranch = () => {
    const storeId = steps?.branch?.primaryStoreId;
    const branchId = steps?.branch?.primaryBranchId;
    if (storeId && branchId) {
      navigate("/view-branch-v1", { state: { storeId, branchId } });
    } else {
      navigate("/store&branches-overview");
    }
  };
  const goMyAccount = () => navigate("/my-account");
  const goAppsManagement = () => navigate("/apps-management");
  const goConnectApp = (appId) => {
    if (appId) navigate(`/add-app/${appId}`);
    else goAppsManagement();
  };
  const goAddon = (appId) => {
    if (appId) {
      navigate(`/addon-intermediary/${appId}`, {
        state: { validAccess: true, isConnected: true },
      });
    } else {
      goAppsManagement();
    }
  };
  const goBranchMapping = () => {
    const storeId = steps?.branchMapping?.primaryStoreId || steps?.pos?.primaryStoreId;
    const branchId = steps?.branchMapping?.primaryBranchId || steps?.pos?.primaryBranchId;
    if (storeId && branchId) {
      navigate("/view-branch-v1", { state: { storeId, branchId } });
    } else {
      navigate("/apps-management");
    }
  };

  if (loading && !steps) {
    return (
      <MDBContainer
        fluid
        className="setup-guide-page d-flex justify-content-center py-5"
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <MDBSpinner />
      </MDBContainer>
    );
  }

  if (!steps || !progress) {
    return (
      <MDBContainer
        fluid
        className="setup-guide-page"
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <p>{ts("loadError")}</p>
      </MDBContainer>
    );
  }

  const isBusinessDone = hasMeaningfulBusinessInfo(steps.businessInfo?.data);
  const storeDataDone = steps.store.status === "complete";
  const branchDataDone = steps.branch.status === "complete";
  const deliveryDataDone = steps.deliveryIntegrations.status === "complete";
  const mappingDataDone = steps.branchMapping.status === "complete";
  const catalogStep = steps.catalog || {
    status: "not_started",
    subSteps: [],
  };
  const posStep = steps.pos || {
    status: "not_started",
    subSteps: [],
  };

  const catalogSubByKey = Object.fromEntries(
    (catalogStep.subSteps || []).map((s) => [s.key, s])
  );
  const posSubByKey = Object.fromEntries((posStep.subSteps || []).map((s) => [s.key, s]));

  const hasCategories =
    catalogSubByKey.categories?.status === "complete" ||
    Number(catalogSubByKey.categories?.count || 0) > 0;
  const hasProducts =
    catalogSubByKey.products?.status === "complete" ||
    Number(catalogSubByKey.products?.count || 0) > 0;
  const hasCatalogWithProducts =
    catalogSubByKey.catalog?.status === "complete" ||
    Number(catalogSubByKey.catalog?.count || 0) > 0;
  const catalogDataDone =
    catalogStep.status === "complete" ||
    hasCategories ||
    hasProducts ||
    hasCatalogWithProducts;
  const posDataDone = posStep.status === "complete";

  // Strict sequence: a step can only count as complete if every prior step is complete.
  const isStoreDone = isBusinessDone && storeDataDone;
  const isBranchDone = isStoreDone && branchDataDone;
  const isDeliveryDone = isBranchDone && deliveryDataDone;
  const isMappingDone = isDeliveryDone && mappingDataDone;
  const isCatalogDone = isMappingDone && catalogDataDone;
  const isPosDone = isCatalogDone && posDataDone;

  const sequentialCompleted = [
    isBusinessDone,
    isStoreDone,
    isBranchDone,
    isDeliveryDone,
    isMappingDone,
    isCatalogDone,
    isPosDone,
  ].filter(Boolean).length;
  const sequentialTotal = 7;
  const progressPct =
    sequentialTotal > 0 ? (sequentialCompleted / sequentialTotal) * 100 : 0;

  const accountStatus =
    steps.businessInfo.accountStatus ||
    steps.businessInfo.data?.accountStatus ||
    null;

  const coreSteps = [
    {
      key: "businessInfo",
      number: 1,
      title: ts("steps.businessInfo.title"),
      desc: ts("steps.businessInfo.desc"),
      status: isBusinessDone ? "complete" : "not_started",
      coreComplete: isBusinessDone,
      locked: false,
      faded: false,
    },
    {
      key: "store",
      number: 2,
      title: ts("steps.store.title"),
      desc: ts("steps.store.desc"),
      status: !isBusinessDone
        ? "locked"
        : isStoreDone
          ? "complete"
          : steps.store.status,
      coreComplete: isStoreDone,
      locked: !isBusinessDone,
      faded: !isBusinessDone,
    },
    {
      key: "branch",
      number: 3,
      title: ts("steps.branch.title"),
      desc: ts("steps.branch.desc"),
      status: !isStoreDone
        ? "locked"
        : isBranchDone
          ? "complete"
          : steps.branch.status,
      coreComplete: isBranchDone,
      locked: !isStoreDone,
      faded: !isStoreDone,
    },
    {
      key: "delivery",
      number: 4,
      title: ts("steps.delivery.title"),
      desc: ts("steps.delivery.desc"),
      status: !isBranchDone
        ? "locked"
        : steps.deliveryIntegrations.locked
          ? "locked"
          : isDeliveryDone
            ? "complete"
            : steps.deliveryIntegrations.status,
      coreComplete: isDeliveryDone,
      locked: !isBranchDone || Boolean(steps.deliveryIntegrations.locked),
      faded: !isBranchDone || Boolean(steps.deliveryIntegrations.locked),
    },
    {
      key: "mapping",
      number: 5,
      title: ts("steps.mapping.title"),
      desc: ts("steps.mapping.desc"),
      status: !isDeliveryDone
        ? "locked"
        : steps.branchMapping.locked
          ? "locked"
          : isMappingDone
            ? "complete"
            : steps.branchMapping.status,
      coreComplete: isMappingDone,
      locked: !isDeliveryDone || Boolean(steps.branchMapping.locked),
      faded: !isDeliveryDone || Boolean(steps.branchMapping.locked),
    },
    {
      key: "catalog",
      number: 6,
      title: ts("steps.catalog.title"),
      desc: ts("steps.catalog.desc"),
      status: !isMappingDone
        ? "locked"
        : isCatalogDone
          ? "complete"
          : catalogStep.status,
      coreComplete: isCatalogDone,
      locked: !isMappingDone,
      faded: !isMappingDone,
    },
    {
      key: "pos",
      number: 7,
      title: ts("steps.pos.title"),
      desc: ts("steps.pos.desc"),
      status: !isCatalogDone
        ? "locked"
        : isPosDone
          ? "complete"
          : posStep.status,
      coreComplete: isPosDone,
      locked: !isCatalogDone,
      faded: !isCatalogDone,
    },
  ];

  return (
    <MDBContainer
      fluid
      className="setup-guide-page"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <h1 className="setup-guide-page__title">{ts("title")}</h1>
      <p className="setup-guide-page__subtitle">{ts("subtitle")}</p>

      <div className="setup-guide-page__progress-row">
        <div className="setup-guide-page__progress-track">
          <div
            className="setup-guide-page__progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="setup-guide-page__progress-label">
          {ts("progressLabel", {
            completed: sequentialCompleted,
            total: sequentialTotal,
          })}
        </span>
      </div>

      <div className="setup-guide-timeline">
        {coreSteps.map((step, index) => (
          <div key={step.key} className="setup-guide-step-row">
            <div className="setup-guide-step-rail">
              <StepCircle
                stepNumber={step.number}
                status={step.status}
                coreComplete={step.coreComplete}
              />
              {index < coreSteps.length - 1 && (
                <div
                  className={`setup-guide-step-line ${
                    step.coreComplete
                      ? "setup-guide-step-line--complete"
                      : "setup-guide-step-line--pending"
                  }`}
                />
              )}
            </div>

            <div
              className={`setup-guide-step-card ${
                step.faded ? "setup-guide-step-card--faded" : ""
              }`}
            >
              <div className="setup-guide-step-card__header">
                <div>
                  <h2 className="setup-guide-step-card__title">
                    {step.number}. {step.title}
                  </h2>
                  <p className="setup-guide-step-card__desc">{step.desc}</p>
                </div>
                <StatusText
                  status={step.coreComplete ? "complete" : step.status}
                  t={t}
                />
              </div>

              {step.key === "businessInfo" && (
                <>
                  {!isBusinessDone && accountStatus === "Under Review" && (
                    <div className="setup-guide-alert">
                      <div>
                        <p className="setup-guide-alert__title">
                          {ts("businessInfo.awaitingTitle")}
                        </p>
                        <p className="setup-guide-alert__body">
                          {ts("businessInfo.awaitingBody")}
                        </p>
                      </div>
                    </div>
                  )}
                  {accountStatus === "Inactive" && (
                    <div className="setup-guide-alert setup-guide-alert--error">
                      <div>
                        <p className="setup-guide-alert__title setup-guide-alert__title--error">
                          {ts("businessInfo.inactiveTitle")}
                        </p>
                        <p className="setup-guide-alert__body setup-guide-alert__body--error">
                          {ts("businessInfo.inactiveBody")}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="setup-guide-step-card__footer">
                    <button
                      type="button"
                      className="setup-guide-btn setup-guide-btn--edit"
                      onClick={goMyAccount}
                    >
                      {ts("actions.edit")}
                    </button>
                  </div>
                </>
              )}

              {step.key === "store" && (
                <div className="setup-guide-step-card__footer">
                  {steps.store.storeCount === 0 ? (
                    <button
                      type="button"
                      className={`setup-guide-btn setup-guide-btn--primary ${
                        step.locked ? "setup-guide-btn--disabled" : ""
                      }`}
                      disabled={step.locked}
                      onClick={goAddStore}
                    >
                      {ts("actions.addStore")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="setup-guide-btn setup-guide-btn--edit"
                      onClick={goEditStore}
                    >
                      {ts("actions.edit")}
                    </button>
                  )}
                </div>
              )}

              {step.key === "branch" && (
                <>
                  {steps.branch.branchCount === 0 && (
                    <div className="setup-guide-step-card__body">
                      <div className="setup-guide-empty">{ts("branch.empty")}</div>
                    </div>
                  )}
                  <div className="setup-guide-step-card__footer">
                    {steps.branch.branchCount === 0 ? (
                      <button
                        type="button"
                        className={`setup-guide-btn setup-guide-btn--primary ${
                          step.locked ? "setup-guide-btn--disabled" : ""
                        }`}
                        disabled={step.locked}
                        onClick={goAddBranch}
                      >
                        {ts("actions.addBranch")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="setup-guide-btn setup-guide-btn--edit"
                        onClick={goEditBranch}
                      >
                        {ts("actions.edit")}
                      </button>
                    )}
                  </div>
                </>
              )}

              {step.key === "delivery" && (
                <>
                  {step.locked && (
                    <p className="setup-guide-step-card__body-note">
                      {ts("delivery.lockedNote")}
                    </p>
                  )}
                  {(steps.deliveryIntegrations.platforms || []).length === 0 ? (
                    <div className="setup-guide-empty">{ts("delivery.empty")}</div>
                  ) : (
                    <div className="setup-guide-table-wrap">
                      <table className="setup-guide-table">
                        <thead>
                          <tr>
                            <th>{ts("deliveryTable.platform")}</th>
                            <th>{ts("deliveryTable.status")}</th>
                            <th>{ts("deliveryTable.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(steps.deliveryIntegrations.platforms || []).map((platform) => {
                            const platformName = getDeliveryPlatformDisplayName(
                              platform,
                              t,
                              isArabic
                            );
                            const resolvedLogo = resolvePlatformLogo(platform, {
                              height: "24px",
                            });
                            const logoSrc =
                              resolvedLogo?.src ||
                              resolveIntegrationLogo(platform) ||
                              platform.logo ||
                              "";
                            const wideLogo = usesWideIntegrationLogo(platform);
                            const canConnect =
                              platform.connectable &&
                              !platform.connected &&
                              !step.locked &&
                              !platform.isAddon;

                            return (
                              <tr key={platform.appId || platform.id}>
                                <td>
                                  <div className="setup-guide-platform-cell">
                                    {logoSrc ? (
                                      <img
                                        src={logoSrc}
                                        alt={resolvedLogo?.alt || platformName}
                                        height={24}
                                        className={`setup-guide-platform-cell__logo${
                                          wideLogo
                                            ? " setup-guide-platform-cell__logo--wide"
                                            : ""
                                        }`}
                                        onError={(e) =>
                                          handlePlatformLogoError(e, platform)
                                        }
                                      />
                                    ) : null}
                                    <span title={platformName}>{platformName}</span>
                                  </div>
                                </td>
                                <td>
                                  <DeliveryPlatformStatus
                                    platform={platform}
                                    ts={ts}
                                    t={t}
                                  />
                                </td>
                                <td>
                                  {platform.isAddon ? (
                                    <button
                                      type="button"
                                      className="setup-guide-connect-btn setup-guide-connect-btn--active"
                                      disabled={step.locked}
                                      onClick={() => goAddon(platform.appId)}
                                    >
                                      {ts("deliveryTable.manage")}
                                    </button>
                                  ) : platform.connectable ? (
                                    <button
                                      type="button"
                                      className={`setup-guide-connect-btn ${
                                        platform.connected
                                          ? ""
                                          : "setup-guide-connect-btn--active"
                                      }`}
                                      disabled={!canConnect && !platform.connected}
                                      onClick={() =>
                                        platform.connected
                                          ? goAppsManagement()
                                          : goConnectApp(platform.appId)
                                      }
                                    >
                                      {platform.connected
                                        ? ts("deliveryTable.connected")
                                        : ts("deliveryTable.connect")}
                                    </button>
                                  ) : (
                                    <span className="setup-guide-table__unavailable">
                                      {getDeliveryPlatformStatusLabel(platform, ts)}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="setup-guide-step-card__footer">
                    <button
                      type="button"
                      className={`setup-guide-btn setup-guide-btn--outline ${
                        step.locked ? "setup-guide-btn--disabled" : ""
                      }`}
                      disabled={step.locked}
                      onClick={goAppsManagement}
                    >
                      {ts("actions.manageApps")}
                    </button>
                  </div>
                </>
              )}

              {step.key === "mapping" && (
                <>
                  {!step.locked &&
                    !step.coreComplete &&
                    steps.branchMapping.mappedCount === 0 && (
                    <div className="setup-guide-empty">{ts("mapping.empty")}</div>
                  )}
                  <div className="setup-guide-step-card__footer">
                    <button
                      type="button"
                      className={`setup-guide-btn ${
                        step.coreComplete
                          ? "setup-guide-btn--edit"
                          : "setup-guide-btn--primary"
                      } ${step.locked ? "setup-guide-btn--disabled" : ""}`}
                      disabled={step.locked}
                      onClick={goBranchMapping}
                    >
                      {step.coreComplete
                        ? ts("actions.edit")
                        : ts("actions.mapBranch")}
                    </button>
                  </div>
                </>
              )}

              {step.key === "catalog" && (
                <div className="setup-guide-substeps">
                  {[
                    {
                      num: 1,
                      key: "categories",
                      title: ts("catalog.createCategories"),
                      desc: ts("catalog.createCategoriesDesc"),
                      done: hasCategories,
                      enabled: !step.locked,
                      label: ts("actions.addCategory"),
                      path: "/category-overview",
                    },
                    {
                      num: 2,
                      key: "products",
                      title: ts("catalog.createProducts"),
                      desc: ts("catalog.createProductsDesc"),
                      done: hasProducts,
                      enabled: !step.locked && hasCategories,
                      label: ts("actions.addProduct"),
                      path: "/products",
                    },
                    {
                      num: 3,
                      key: "catalog",
                      title: ts("catalog.createCatalog"),
                      desc: ts("catalog.createCatalogDesc"),
                      done: hasCatalogWithProducts,
                      enabled: !step.locked && hasProducts,
                      label: ts("actions.createCatalog"),
                      path: "/catalog",
                    },
                  ].map((mini) => (
                    <div key={mini.key} className="setup-guide-substep">
                      <SubStepCircle stepNumber={mini.num} complete={mini.done} />
                      <div className="setup-guide-substep__body">
                        <div className="setup-guide-substep__header">
                          <p className="setup-guide-substep__title">{mini.title}</p>
                          <StatusText
                            status={
                              step.locked
                                ? "locked"
                                : mini.done
                                  ? "complete"
                                  : "not_started"
                            }
                            t={t}
                          />
                        </div>
                        <p className="setup-guide-substep__desc">{mini.desc}</p>
                        <button
                          type="button"
                          className={`setup-guide-btn ${
                            mini.done
                              ? "setup-guide-btn--edit"
                              : "setup-guide-btn--primary"
                          } ${!mini.enabled && !mini.done ? "setup-guide-btn--disabled" : ""}`}
                          disabled={step.locked || (!mini.enabled && !mini.done)}
                          onClick={() => navigate(mini.path)}
                        >
                          {mini.done ? ts("actions.edit") : mini.label}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {step.key === "pos" && (
                <div className="setup-guide-substeps">
                  {[
                    {
                      num: 1,
                      key: "authorize",
                      title: ts("pos.authorize"),
                      desc: ts("pos.authorizeDesc"),
                      done: posSubByKey.authorize?.status === "complete",
                      mode: "connect",
                    },
                    {
                      num: 2,
                      key: "mapBranches",
                      title: ts("pos.mapBranches"),
                      desc: ts("pos.mapBranchesDesc"),
                      done: posSubByKey.mapBranches?.status === "complete",
                      mode: "map",
                    },
                  ].map((mini) => {
                    const platforms =
                      steps.pos?.availablePlatforms ||
                      steps.posIntegration?.availablePlatforms ||
                      [];
                    const authorizeDone =
                      posSubByKey.authorize?.status === "complete";
                    const sectionEnabled =
                      !step.locked &&
                      (mini.mode === "connect" || authorizeDone);

                    return (
                      <div key={mini.key} className="setup-guide-substep">
                        <SubStepCircle
                          stepNumber={mini.num}
                          complete={mini.done}
                        />
                        <div className="setup-guide-substep__body">
                          <div className="setup-guide-substep__header">
                            <p className="setup-guide-substep__title">
                              {mini.title}
                            </p>
                            <StatusText
                              status={
                                step.locked
                                  ? "locked"
                                  : mini.done
                                    ? "complete"
                                    : "not_started"
                              }
                              t={t}
                            />
                          </div>
                          <p className="setup-guide-substep__desc">{mini.desc}</p>

                          {platforms.length === 0 ? (
                            <button
                              type="button"
                              className={`setup-guide-btn setup-guide-btn--primary ${
                                !sectionEnabled ? "setup-guide-btn--disabled" : ""
                              }`}
                              disabled={!sectionEnabled}
                              onClick={goAppsManagement}
                            >
                              {mini.mode === "connect"
                                ? ts("actions.connectPos")
                                : ts("actions.mapPosBranch")}
                            </button>
                          ) : (
                            <div className="setup-guide-pos-platforms">
                              {platforms.map((platform) => {
                                const platformName =
                                  getDeliveryPlatformDisplayName(
                                    platform,
                                    t,
                                    isArabic
                                  );
                                const logoSrc =
                                  resolvePlatformLogo(platform)?.src ||
                                  resolveIntegrationLogo(platform) ||
                                  platform.logo ||
                                  "";
                                const connected = Boolean(platform.connected);
                                const canConnect =
                                  sectionEnabled &&
                                  mini.mode === "connect" &&
                                  !connected;
                                const canMap =
                                  sectionEnabled &&
                                  mini.mode === "map" &&
                                  connected;

                                return (
                                  <div
                                    key={platform.appId || platform.id}
                                    className="setup-guide-pos-platform-row"
                                  >
                                    <div className="setup-guide-platform-cell">
                                      {logoSrc ? (
                                        <img
                                          src={logoSrc}
                                          alt={platformName}
                                          className="setup-guide-platform-cell__logo"
                                          onError={(e) =>
                                            handlePlatformLogoError(e, platform)
                                          }
                                        />
                                      ) : null}
                                      <span title={platformName}>
                                        {platformName}
                                      </span>
                                    </div>
                                    {mini.mode === "connect" ? (
                                      <button
                                        type="button"
                                        className={`setup-guide-btn ${
                                          connected
                                            ? "setup-guide-btn--edit"
                                            : "setup-guide-btn--primary"
                                        } ${
                                          !canConnect && !connected
                                            ? "setup-guide-btn--disabled"
                                            : ""
                                        }`}
                                        disabled={!canConnect && !connected}
                                        onClick={() =>
                                          connected
                                            ? goAppsManagement()
                                            : goConnectApp(platform.appId)
                                        }
                                      >
                                        {connected
                                          ? t("connected")
                                          : ts("actions.connectPos")}
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className={`setup-guide-btn ${
                                          mini.done
                                            ? "setup-guide-btn--edit"
                                            : "setup-guide-btn--primary"
                                        } ${
                                          !canMap ? "setup-guide-btn--disabled" : ""
                                        }`}
                                        disabled={!canMap}
                                        onClick={goBranchMapping}
                                      >
                                        {mini.done
                                          ? ts("actions.edit")
                                          : ts("actions.mapPosBranch")}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </MDBContainer>
  );
}

export default SetupGuidePage;
